import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import type { SelectionField } from '../../state/useSelectionContext';
import { Frame, SubCard } from './primitives';

/**
 * campaign_goal (docs/momentum-v3-slides.md "campaign_goal"): FULL light,
 * NEW slide, replaces `agenda` as the default v2 spot. This is a v3-only
 * section type not yet in `DocumentSectionType` (src/domain/types.ts) -
 * stream 1 adds it server-side; until that lands the registry widens the
 * switch discriminant with a narrow cast (see `registry.tsx`). Nothing here
 * depends on the cast - the component only ever reads `content.headline`
 * and `content.fields.priority1..4`, both already-generic
 * `DocumentSectionContent` shape.
 *
 * Goal statement dominates (display-scale headline, left-aligned); 2-4
 * compact priority cards sit below it. Workspace (readOnly === false) always
 * shows all four card slots so a rep can fill any of them; print/share/
 * presentation (readOnly === true) renders only the ones with content, so an
 * unfilled priority never appears as empty chrome in front of a client.
 *
 * The priorities read as a checklist: a check badge on each card, so the
 * client sees at a glance what the campaign is set up to achieve. An empty workspace slot shows an
 * unticked box instead of the check.
 */
function CheckBadge({ checked }: { checked: boolean }): ReactElement {
  return (
    <span className={`mv3-goal-check${checked ? ' is-checked' : ''}`} aria-hidden="true">
      {checked ? (
        <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12.5l4.5 4.5L19 7.5" />
        </svg>
      ) : null}
    </span>
  );
}

const PRIORITY_KEYS = ['priority1', 'priority2', 'priority3', 'priority4'] as const;

export function CampaignGoalPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const fields = section.content.fields ?? {};
  const setField = (key: string, value: string) => {
    actions.saveContent(section, { fields: { ...fields, [key]: value } });
  };

  const slots = PRIORITY_KEYS.map((key) => ({ key, value: fields[key] }));
  const visibleSlots = readOnly ? slots.filter((slot) => Boolean(slot.value?.trim())) : slots;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-goal-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-goal-pane">
          <div className="mv3-goal-statement">
            <EditableText
              value={section.content.headline}
              field="headline"
              placeholder="What is this campaign trying to accomplish?"
              readOnly={readOnly}
              variant="headline"
              onSave={(headline) => actions.saveContent(section, { headline })}
            />
          </div>
          {visibleSlots.length > 0 ? (
            <div
              className="mv3-goal-cards"
              style={{ gridTemplateColumns: `repeat(${visibleSlots.length}, 1fr)` }}
            >
              {visibleSlots.map((slot, index) => (
                <SubCard tone="light" key={slot.key} className="mv3-goal-card">
                  <div className="mv3-goal-card-head">
                    <CheckBadge checked={Boolean(slot.value?.trim())} />
                    <span className="mv3-goal-card-num">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <EditableText
                    value={slot.value}
                    field={`fields.${slot.key}` as SelectionField}
                    placeholder="Add a supporting priority"
                    readOnly={readOnly}
                    onSave={(next) => setField(slot.key, next)}
                  />
                </SubCard>
              ))}
            </div>
          ) : null}
        </div>
      </Frame>
    </PageShell>
  );
}
